/**
 * Error Reporting Service
 * Sends frontend errors to backend audit system
 *
 * IMPORTANT: Includes recursion protection to prevent infinite loops
 * when error reporting itself fails.
 */

import axios, { AxiosInstance } from 'axios';

// Create a separate axios instance for error reporting to avoid circular dependencies
const errorReportingClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Module-level flag to prevent recursive error reporting
let isReportingError = false;

interface ErrorReport {
  error_type: string;
  error_message: string;
  stack_trace?: string;
  endpoint?: string;
  component?: string;
  user_agent?: string;
  username?: string;  // Username from localStorage
  context?: Record<string, unknown>;
}

/**
 * Get username from localStorage auth data
 */
const getUsername = (): string | undefined => {
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.username || parsed.user?.username;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Report error to backend audit system
 *
 * CRITICAL: Has recursion protection to prevent infinite loops when
 * error reporting itself fails (e.g., network errors).
 */
export const reportError = async (error: Error | string, context?: {
  component?: string;
  endpoint?: string;
  additionalContext?: Record<string, unknown>;
}): Promise<void> => {
  // RECURSION GUARD: If we're already reporting, silently exit
  if (isReportingError) {
    console.warn('[ErrorReporting] Suppressed recursive error report');
    return;
  }

  try {
    isReportingError = true;  // Set flag BEFORE any async operation

    const errorReport: ErrorReport = {
      error_type: typeof error === 'string' ? 'FrontendError' : error.constructor.name,
      error_message: typeof error === 'string' ? error : error.message,
      stack_trace: typeof error === 'string' ? undefined : error.stack,
      component: context?.component,
      endpoint: context?.endpoint || window.location.pathname,
      user_agent: navigator.userAgent,
      username: getUsername(),
      context: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...context?.additionalContext
      }
    };

    // Send to backend audit endpoint
    await errorReportingClient.post('/audit/frontend-error', errorReport);
  } catch (reportingError) {
    // NEVER report errors from error reporting - just log locally
    console.error('[ErrorReporting] Failed to report error (not retrying):', reportingError);
  } finally {
    isReportingError = false;  // Always reset flag
  }
};

/**
 * Report network error with request/response details
 *
 * CRITICAL: Has recursion protection.
 */
export const reportNetworkError = async (error: unknown, config?: {
  url?: string;
  method?: string;
  requestData?: unknown;
  responseData?: unknown;
  statusCode?: number;
}): Promise<void> => {
  // RECURSION GUARD: If we're already reporting, silently exit
  if (isReportingError) {
    console.warn('[ErrorReporting] Suppressed recursive network error report');
    return;
  }

  try {
    isReportingError = true;  // Set flag BEFORE any async operation

    const errorObj = error instanceof Error ? error : new Error(String(error));
    const errorReport: ErrorReport = {
      error_type: 'NetworkError',
      error_message: errorObj.message || 'Network request failed',
      stack_trace: errorObj.stack,
      endpoint: config?.url || window.location.pathname,
      component: 'NetworkLayer',
      user_agent: navigator.userAgent,
      username: getUsername(),
      context: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        method: config?.method,
        requestData: config?.requestData,
        responseData: config?.responseData,
        statusCode: config?.statusCode
      }
    };

    await errorReportingClient.post('/audit/frontend-error', errorReport);
  } catch (reportingError) {
    // NEVER report errors from error reporting - just log locally
    console.error('[ErrorReporting] Failed to report network error (not retrying):', reportingError);
  } finally {
    isReportingError = false;  // Always reset flag
  }
};

/**
 * Setup global error handlers
 *
 * Installs handlers for:
 * - Unhandled errors (window.onerror)
 * - Unhandled promise rejections
 * - Console.error calls (optional, can be noisy)
 */
export const setupGlobalErrorHandlers = (): void => {
  // Catch unhandled errors
  window.onerror = (message, source, lineno, colno, error) => {
    // Don't report if already reporting (prevents infinite loops)
    if (isReportingError) return false;

    reportError(error || new Error(String(message)), {
      component: 'GlobalErrorHandler',
      additionalContext: {
        source,
        lineno,
        colno
      }
    });
    return false; // Let default error handling continue
  };

  // Catch unhandled promise rejections
  window.onunhandledrejection = (event) => {
    // Don't report if already reporting (prevents infinite loops)
    if (isReportingError) return;

    reportError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      {
        component: 'UnhandledPromiseRejection',
        additionalContext: {
          type: 'unhandledrejection'
        }
      }
    );
  };

  // Note: We intentionally do NOT intercept console.error
  // because it can be very noisy and cause performance issues.
  // If you need console.error reporting, uncomment below:
  /*
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    // Call original console.error
    originalConsoleError.apply(console, args);

    // Don't report if already reporting (prevents infinite loops)
    if (isReportingError) return;

    // Report to audit system
    const errorMessage = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');

    reportError(errorMessage, {
      component: 'ConsoleError',
      additionalContext: {
        arguments: args
      }
    });
  };
  */

  console.log('[ErrorReporting] Global error handlers initialized');
};
